/**
 * Cable Fiber Usage Dialog - Display fiber usage statistics for a cable
 */
import React, { useEffect, useState } from 'react';
import { CableFiberUsage } from '../../types';
import { apiClient } from '../../services/api';
import './Dialog.css';
import './CableFiberUsageDialog.css';

interface CableFiberUsageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
  cableId: number;
}

export const CableFiberUsageDialog: React.FC<CableFiberUsageDialogProps> = ({
  isOpen,
  onClose,
  projectId,
  cableId,
}) => {
  const [usage, setUsage] = useState<CableFiberUsage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && cableId) {
      loadFiberUsage();
    }
  }, [isOpen, cableId, projectId]);

  const loadFiberUsage = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.cables.getFiberUsage(projectId, cableId);
      setUsage(response.data);
    } catch (err: any) {
      console.error('[CableFiberUsageDialog] Error loading fiber usage:', err);
      setError(err.response?.data?.detail || 'Nepodarilo sa načítať využitie vlákien');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const formatFiberList = (fibers: number[]): string => {
    if (fibers.length === 0) return '-';
    if (fibers.length <= 5) return fibers.join(', ');
    
    // Group consecutive fibers
    const groups: string[] = [];
    let start = fibers[0];
    let prev = fibers[0];
    
    for (let i = 1; i <= fibers.length; i++) {
      if (i === fibers.length || fibers[i] !== prev + 1) {
        if (start === prev) {
          groups.push(`${start}`);
        } else if (prev === start + 1) {
          groups.push(`${start}, ${prev}`);
        } else {
          groups.push(`${start}-${prev}`);
        }
        if (i < fibers.length) {
          start = fibers[i];
          prev = fibers[i];
        }
      } else {
        prev = fibers[i];
      }
    }
    
    return groups.join(', ');
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog cable-fiber-usage-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>📊 Využitie vlákien v kábli</h2>
          <button className="dialog-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="dialog-content">
          {loading && (
            <div className="loading-message">
              ⏳ Načítavam údaje...
            </div>
          )}

          {error && (
            <div className="error-message">
              ⚠️ {error}
            </div>
          )}

          {usage && !loading && (
            <>
              <div className="cable-info">
                <div className="info-row">
                  <span className="label">Od:</span>
                  <span className="value">{usage.from_pole.name}</span>
                </div>
                <div className="info-row">
                  <span className="label">Do:</span>
                  <span className="value">{usage.to_pole.name}</span>
                </div>
                <div className="info-row">
                  <span className="label">Počet vlákien:</span>
                  <span className="value">{usage.fiber_count}</span>
                </div>
              </div>

              <div className="usage-stats">
                <div className="stat-card fully-used">
                  <div className="stat-icon">✅</div>
                  <div className="stat-content">
                    <div className="stat-label">Použité vlákna</div>
                    <div className="stat-value">
                      {usage.statistics.used_count} ({usage.statistics.used_percent}%)
                    </div>
                    <div className="stat-description">Pripojené na oboch koncoch</div>
                    {usage.used.length > 0 && (
                      <div className="stat-detail">
                        Vlákna: {formatFiberList(usage.used)}
                      </div>
                    )}
                  </div>
                </div>

                <div className="stat-card free-ends">
                  <div className="stat-icon">⚠️</div>
                  <div className="stat-content">
                    <div className="stat-label">Polospojené vlákna</div>
                    <div className="stat-value">
                      {usage.statistics.half_connected_count} ({usage.statistics.half_connected_percent}%)
                    </div>
                    <div className="stat-description">Pripojené len na jednom konci</div>
                    {usage.half_connected.in_from_pole.length > 0 && (
                      <div className="stat-detail">
                        Voľný koniec v {usage.from_pole.name}: {formatFiberList(usage.half_connected.in_from_pole)}
                      </div>
                    )}
                    {usage.half_connected.in_to_pole.length > 0 && (
                      <div className="stat-detail">
                        Voľný koniec v {usage.to_pole.name}: {formatFiberList(usage.half_connected.in_to_pole)}
                      </div>
                    )}
                  </div>
                </div>

                <div className="stat-card unused">
                  <div className="stat-icon">🟢</div>
                  <div className="stat-content">
                    <div className="stat-label">Voľné vlákna</div>
                    <div className="stat-value">
                      {usage.statistics.free_count} ({usage.statistics.free_percent}%)
                    </div>
                    <div className="stat-description">Nepripojené, dostupné pre použitie</div>
                    {usage.free.length > 0 && usage.free.length <= 10 && (
                      <div className="stat-detail">
                        Vlákna: {formatFiberList(usage.free)}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="usage-chart">
                <div className="chart-bar">
                  {usage.statistics.used_percent > 0 && (
                    <div
                      className="chart-segment fully-used"
                      style={{ width: `${usage.statistics.used_percent}%` }}
                      title={`Použité: ${usage.statistics.used_percent}%`}
                    />
                  )}
                  {usage.statistics.half_connected_percent > 0 && (
                    <div
                      className="chart-segment free-ends"
                      style={{ width: `${usage.statistics.half_connected_percent}%` }}
                      title={`Polospojené: ${usage.statistics.half_connected_percent}%`}
                    />
                  )}
                  {usage.statistics.free_percent > 0 && (
                    <div
                      className="chart-segment unused"
                      style={{ width: `${usage.statistics.free_percent}%` }}
                      title={`Voľné: ${usage.statistics.free_percent}%`}
                    />
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="dialog-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Zavrieť
          </button>
        </div>
      </div>
    </div>
  );
};

